import type { ProposedAction } from '../core/types';

/** actionId → selected. Ids missing from the map read as selected. */
type SelectionMap = Map<number, boolean>;

interface PrevActionSnapshot {
  id: number;
  executionOrder: number;
  isSelected: boolean;
}

interface ReconcileSelectionStateResult {
  /** New selection map to apply, or `null` if the current one should be left alone. */
  nextSelection: SelectionMap | null;
  /** Updated snapshot to store for the next comparison. */
  nextPrevActions: PrevActionSnapshot[];
}

/**
 * Reconciles selection state across an action-ID change (versioning,
 * additions/removals, server-driven selection changes) by comparing the
 * previous action snapshot against the current actions. Maps old selection
 * state to new IDs by execution_order (position) to preserve user intent.
 *
 * Pure function: takes the current selection map and the previous/current
 * action lists, and returns either a new selection map to apply or `null`
 * when nothing needs to change.
 *
 * `proposalChanged` is the authoritative "this is a different proposal" signal.
 * A reused panel that switches proposals keeps the same hook instance, and two
 * unrelated proposals can reuse the same `execution_order` positions — so the
 * order-overlap heuristic below cannot tell "versioned same proposal" from
 * "different proposal". When identity says the proposal changed, reset to the
 * new proposal's server-provided selection outright, never mapping the prior
 * proposal's selection (e.g. a deselection) onto it by position.
 */
export function reconcileSelectionState(
  prevActions: PrevActionSnapshot[],
  currentActions: ProposedAction[],
  currentSelection: SelectionMap,
  proposalChanged: boolean
): ReconcileSelectionStateResult {
  const nextPrevActions = currentActions.map(a => ({
    id: a.id,
    executionOrder: a.execution_order,
    isSelected: a.is_selected ?? true,
  }));

  // Different proposal (by identity): discard prior selection entirely and
  // adopt the new proposal's server values. Checked before every heuristic
  // below so no position mapping can leak across a proposal switch.
  if (proposalChanged) {
    const newSelection = new Map<number, boolean>();
    for (const action of currentActions) {
      newSelection.set(action.id, action.is_selected ?? true);
    }
    return { nextSelection: newSelection, nextPrevActions };
  }

  // Check if any is_selected values changed on server (e.g., AI deselected via update_proposal)
  // This must be checked BEFORE the fast path to ensure server selection changes are detected
  const hasServerSelectionChanges = currentActions.some(a => {
    const prevAction = prevActions.find(p => p.id === a.id);
    return (
      prevAction !== undefined &&
      prevAction.isSelected !== (a.is_selected ?? true)
    );
  });

  // FAST PATH: If action IDs are exactly the same (same count, same order) AND
  // no server selection changes, preserve local selection state entirely.
  // This handles the common case of API refresh after selection change where IDs don't change.
  const prevIdArray = prevActions.map(a => a.id);
  const currentIdArray = currentActions.map(a => a.id);
  const idsIdentical =
    prevIdArray.length === currentIdArray.length &&
    prevIdArray.every((id, idx) => id === currentIdArray[idx]);

  if (idsIdentical && !hasServerSelectionChanges) {
    // IDs unchanged and no server selection changes - preserve local selection, just update ref
    return { nextSelection: null, nextPrevActions };
  }

  // Check if this is a completely different set of actions (e.g., switching proposals)
  // If there's NO overlap in action IDs AND no overlap in execution_order positions,
  // we should reset to server values entirely.
  // Note: versioning creates new IDs but preserves execution_order, so we check both.
  const prevIds = new Set(prevActions.map(a => a.id));
  const currentIds = new Set(currentActions.map(a => a.id));
  const prevOrders = new Set(prevActions.map(a => a.executionOrder));
  const hasAnyIdOverlap = currentActions.some(a => prevIds.has(a.id));
  const hasAnyOrderOverlap = currentActions.some(a =>
    prevOrders.has(a.execution_order)
  );

  if (
    !hasAnyIdOverlap &&
    !hasAnyOrderOverlap &&
    currentActions.length > 0 &&
    prevActions.length > 0
  ) {
    // Completely different proposal - reset to server values
    const newSelection = new Map<number, boolean>();
    for (const action of currentActions) {
      newSelection.set(action.id, action.is_selected ?? true);
    }
    return { nextSelection: newSelection, nextPrevActions };
  }

  // Build map of execution_order -> old action ID for quick lookup
  const prevIdByOrder = new Map<number, number>();
  for (const a of prevActions) {
    prevIdByOrder.set(a.executionOrder, a.id);
  }

  // Check if any IDs changed (versioning: same execution_order but different ID)
  const hasIdChanges = currentActions.some(a => {
    const prevId = prevIdByOrder.get(a.execution_order);
    return prevId !== undefined && prevId !== a.id;
  });

  // Check if new actions were added (execution_order not in previous state)
  const hasNewActions = currentActions.some(
    a => !prevIdByOrder.has(a.execution_order)
  );

  // Check if actions were removed (previous ID not in current actions)
  const hasRemovedActions = prevActions.some(p => !currentIds.has(p.id));

  // Check if any is_selected values changed on server (e.g., AI deselected via update_proposal)
  // Match by ID first, then fall back to execution_order for versioned actions with new IDs
  const hasSelectionChanges = currentActions.some(a => {
    const prevAction =
      prevActions.find(p => p.id === a.id) ??
      prevActions.find(p => p.executionOrder === a.execution_order);
    return (
      prevAction !== undefined &&
      prevAction.isSelected !== (a.is_selected ?? true)
    );
  });

  if (
    !hasIdChanges &&
    !hasNewActions &&
    !hasRemovedActions &&
    !hasSelectionChanges
  ) {
    return { nextSelection: null, nextPrevActions };
  }

  // Map old selection state to new IDs by execution_order
  const newSelection = new Map<number, boolean>();
  for (const action of currentActions) {
    const prevId = prevIdByOrder.get(action.execution_order);
    // Check if this specific action's is_selected changed on server
    // Match by ID first, then fall back to execution_order for versioned actions
    const prevAction =
      prevActions.find(p => p.id === action.id) ??
      prevActions.find(p => p.executionOrder === action.execution_order);
    const thisActionSelectionChanged =
      prevAction !== undefined &&
      prevAction.isSelected !== (action.is_selected ?? true);

    if (thisActionSelectionChanged) {
      // Server selection state changed for THIS action - trust server
      newSelection.set(action.id, action.is_selected ?? true);
    } else if (currentSelection.has(action.id)) {
      // Same action ID exists - preserve its selection state directly
      newSelection.set(action.id, currentSelection.get(action.id) ?? true);
    } else if (prevId !== undefined && currentSelection.has(prevId)) {
      // Different ID but same execution_order - this is versioning (new ID for same position)
      // Transfer selection state from old ID to new ID
      newSelection.set(action.id, currentSelection.get(prevId) ?? true);
    } else {
      // New action or no previous state - use server value
      newSelection.set(action.id, action.is_selected ?? true);
    }
  }

  return { nextSelection: newSelection, nextPrevActions };
}
